import { Link } from "react-router-dom";
import { BRAND, VALUES } from "../lib/brand";
import TrustSection from "../components/TrustSection";
import useSeo from "../lib/useSeo";

const PageHeader = () => (
  <section className="border-b border-bos-ice" data-testid="about-header">
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-24 pb-20 lg:pt-32 lg:pb-24">
      <p className="bos-eyebrow">About the studio</p>
      <h1 className="mt-6 text-4xl sm:text-5xl lg:text-[64px] font-medium text-bos-navy tracking-tight leading-[1.08] max-w-3xl">
        Strategy work with the <span className="text-bos-primary">people doing the thinking.</span>
      </h1>
      <p className="mt-8 max-w-2xl text-[17px] text-bos-charcoal/85 leading-[1.7]">
        BuildOnce Studio is a boutique studio built for depth over volume.
        You work directly with the people doing the thinking, so the work
        stays close to the real questions. We move at the speed of good
        decisions.
      </p>
    </div>
  </section>
);

const FounderNote = () => (
  <section className="border-b border-bos-ice" data-testid="about-founder">
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-[1fr_2fr] gap-16">
      <div className="lg:sticky lg:top-28 self-start">
        <div className="aspect-square w-full max-w-[360px] bg-bos-ice border border-bos-light/60 rounded-sm relative overflow-hidden">
          <img
            src={BRAND.founderPhotoUrl}
            alt="Jophy Orenza, founder of BuildOnce Studio"
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="founder-photo"
          />
          <div className="absolute bottom-0 left-0 right-0 px-6 py-4 bg-bos-white/90 backdrop-blur-sm border-t border-bos-ice">
            <div className="text-[15px] font-medium text-bos-navy">Jophy Orenza</div>
            <div className="text-[12px] text-bos-charcoal/60 tracking-[0.04em]">
              Founder · Strategy and positioning
            </div>
          </div>
        </div>
      </div>
      <div>
        <p className="bos-eyebrow">What I kept seeing</p>
        <h2 className="mt-4 text-3xl lg:text-[40px] font-medium text-bos-navy tracking-tight leading-[1.15]">
          The strongest businesses I worked with had one thing in common:
          clarity about what they were really for.
        </h2>
        <div className="mt-8 space-y-6 text-[16px] text-bos-charcoal/85 leading-[1.8] max-w-2xl">
          <p>
            I have spent 20+ years of experience in business development,
            brand strategy, and culture advisory, working independently with
            founder-led businesses, expert-led consultancies, and leadership
            teams across Singapore and Southeast Asia trying to make sense of
            a moving picture. What I noticed, again and again, was that the
            businesses that grew with intention were not the ones with the
            loudest plans. They were the ones with the steadiest foundations.
          </p>
        </div>

        <div className="mt-14">
          <p className="bos-eyebrow">Why BuildOnce exists</p>
        </div>
        <div className="mt-6 space-y-6 text-[16px] text-bos-charcoal/85 leading-[1.8] max-w-2xl">
          <p>
            BuildOnce is the studio I wanted to find when I was on the other
            side of the table. Honest first conversations. Structured thinking.
            Outputs you can actually use. And the kind of working relationship
            where it is fine to admit you are not sure yet.
          </p>
          <p>
            The Clarity Kit was built from the kind of conversations we kept
            having with professionals and founders: people with real
            expertise, several possible directions, and a need to decide what
            to build first before spending more money on branding, websites,
            content, or tools.
          </p>
          <p>
            <Link
              to="/clarity-kit"
              data-testid="about-founder-bridge-cta"
              className="inline-flex items-center gap-2 text-bos-primary bos-link-underline"
            >
              Start with the Clarity Kit
            </Link>
          </p>
        </div>
      </div>
    </div>
  </section>
);

const HowIThinkAboutAI = () => (
  <section className="border-b border-bos-ice bg-bos-ice/40" data-testid="about-how-i-think-about-ai">
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-[1fr_2fr] gap-16">
      <div>
        <p className="bos-eyebrow">Where AI fits</p>
        <span className="bos-divider mt-6" />
      </div>
      <div>
        <h2 className="text-3xl lg:text-[40px] font-medium text-bos-navy tracking-tight leading-[1.15]">
          AI helps you reflect. You still decide.
        </h2>
        <p className="mt-7 text-[16px] text-bos-charcoal/85 leading-[1.8] max-w-2xl">
          AI can help us move faster. But speed is not the same as clarity. At
          BuildOnce, we use AI as a tool for reflection, pattern-spotting, and
          sharper questions. AI helps you reflect. You still decide.
        </p>
      </div>
    </div>
  </section>
);

const HowIWork = () => (
  <section className="border-b border-bos-ice" data-testid="about-how-i-work">
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-[1fr_2fr] gap-16">
      <div className="lg:sticky lg:top-28 self-start">
        <p className="bos-eyebrow">How I work</p>
        <span className="bos-divider mt-6" />
        <p className="mt-8 text-[14px] text-bos-charcoal/70 leading-[1.7] max-w-xs">
          Start with what is true. Work through what is possible. Hand the
          decision back to the person who has to live with it.
        </p>
      </div>
      <div>
        <h2 className="text-3xl lg:text-[40px] font-medium text-bos-navy tracking-tight leading-[1.15]">
          What I love most about walking into a new engagement is what I find
          before the strategy conversation even begins. The people, and
          everything they already know.
        </h2>
        <div className="mt-8 space-y-6 text-[16px] text-bos-charcoal/85 leading-[1.8] max-w-2xl">
          <p>
            I remember sitting with the leadership team of an international
            academy in Singapore. They had called me in for growth strategy.
            What I found was more layered than that. A business with real
            momentum and a team that genuinely cared, but moving in too many
            directions at once, faster than anyone had stopped to ask which
            direction mattered most. The brand had something to say. The growth
            strategy had real promise. But none of it was connected yet, and
            the market could not feel any of it.
          </p>
          <p>
            What I did was not guess. I asked questions. I sat with people at
            every level and asked them what they actually thought, not what
            they thought I wanted to hear. I gathered facts. I mapped the
            options. And then I helped them see not just what was possible, but
            what made sense right now, given where they actually stood.
          </p>
          <p>
            From there, it was not my decision to make. It never is. My role is
            to surface what is actually true, lay out the realistic options,
            and give founders what they need to decide with confidence, not
            just conviction. And when things shift, because they always do, we
            go back to the facts, reassess, and find the next right move
            together.
          </p>
          <p className="text-bos-navy font-medium">
            That is what I do. Start with what is true. Work through what is
            possible. And hand the decision back to the person who has to live
            with it.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const WhoThisIsFor = () => (
  <section className="border-b border-bos-ice bg-bos-ice/40" data-testid="about-who-this-is-for">
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-[1fr_2fr] gap-16">
      <div>
        <p className="bos-eyebrow">Who this is for</p>
        <span className="bos-divider mt-6" />
      </div>
      <div>
        <h2 className="text-3xl lg:text-[40px] font-medium text-bos-navy tracking-tight leading-[1.15]">
          For founders who are moving fast but building on unclear ground.
        </h2>
        <div className="mt-8 space-y-6 text-[16px] text-bos-charcoal/85 leading-[1.8] max-w-2xl">
          <p>
            For leaders who know something is off but cannot quite name it
            yet. For businesses that have built something real and now need
            someone to help them see what comes next, and in what order.
          </p>
          <p>
            I ask before I prescribe. I say the uncomfortable thing when it
            needs to be said. And I stay until the work is something you can
            actually use.
          </p>
          <p className="text-[15px] text-bos-charcoal/75">
            We are not the right fit for every business, and that is the
            point.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const Values = () => (
  <section className="border-b border-bos-ice bg-bos-ice/40" data-testid="about-values">
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24">
      <p className="bos-eyebrow">The BuildOnce way</p>
      <h2 className="mt-4 text-3xl lg:text-[44px] font-medium text-bos-navy tracking-tight leading-[1.15] max-w-3xl">
        Three values we hold ourselves to, in every engagement.
      </h2>
      <div className="mt-14 grid lg:grid-cols-3 gap-6">
        {VALUES.map((v, i) => (
          <div
            key={v.title}
            data-testid={`about-value-${i}`}
            className="bg-bos-white border border-bos-ice p-8 rounded-sm"
          >
            <div className="text-bos-mid text-[12px] tracking-[0.18em] uppercase">
              Value {String(i + 1).padStart(2, "0")}
            </div>
            <h3 className="mt-5 text-[22px] font-medium text-bos-navy">
              {v.title}
            </h3>
            <p className="mt-4 text-[15px] text-bos-charcoal/85 leading-[1.7]">
              {v.body}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const HowWeEngage = () => {
  const steps = [
    {
      title: "Listen, before we prescribe",
      body: "The first hour is for understanding what is actually true about your work, your market, and your moment.",
    },
    {
      title: "Work in the open",
      body: "We share thinking as it forms. No reveal moments. You see the foundation being built.",
    },
    {
      title: "Outputs you can use",
      body: "Documents are for decisions, not decoration. You leave with positioning, messaging, and offers your team can act on.",
    },
    {
      title: "Handoff, not dependency",
      body: "We finish the work, then step back. If you want ongoing advisory after that, we offer it on purpose, not by default.",
    },
  ];
  return (
    <section className="border-b border-bos-ice" data-testid="about-engage">
      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-16">
          <div>
            <p className="bos-eyebrow">How we engage</p>
            <h2 className="mt-4 text-3xl lg:text-[40px] font-medium text-bos-navy tracking-tight leading-[1.15]">
              We choose who we work with carefully. So do our clients.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-12">
            {steps.map((s, i) => (
              <div key={s.title} data-testid={`engage-step-${i}`}>
                <div className="text-bos-mid text-[14px] font-medium">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="mt-2 text-[20px] font-medium text-bos-navy">
                  {s.title}
                </h3>
                <p className="mt-3 text-[14px] text-bos-charcoal/80 leading-[1.7]">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Cta = () => (
  <section className="bg-bos-navy" data-testid="about-cta">
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-20 lg:py-24 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
      <h2 className="text-2xl lg:text-[36px] font-medium text-bos-white tracking-tight leading-[1.15] max-w-2xl">
        Curious whether we are the right studio for the season you are in?
      </h2>
      <Link
        to="/booking"
        data-testid="about-cta-book"
        className="inline-flex items-center bg-bos-white text-bos-navy px-7 py-3.5 rounded-full hover:bg-bos-ice transition-colors self-start lg:self-auto"
      >
        Start the conversation
      </Link>
    </div>
  </section>
);

export default function About() {
  useSeo({
    title: "About Jophy Orenza · BuildOnce Studio",
    description:
      "Jophy Orenza, founder of BuildOnce Studio. Twenty years in business development, brand strategy and culture advisory across Singapore and Southeast Asia.",
  });
  return (
    <div data-testid="about-page">
      <PageHeader />
      <FounderNote />
      <HowIWork />
      <HowIThinkAboutAI />
      <WhoThisIsFor />
      <TrustSection variant="about" />
      <Values />
      <HowWeEngage />
      <Cta />
    </div>
  );
}
